/** biome-ignore-all lint/a11y/noStaticElementInteractions: allow click handlers on <div> */
import React from "react";
import { useDrag } from "react-dnd";
import { Icon } from "@showitinc/showit-wc";
import AppConstants from "../../constants/AppConstants";
import AssetUtils from "../../utils/AssetUtils";
import EventUtils from "../../utils/EventUtils";
import { copyToClipboard } from "../../utils/AppUtils";
import { generateDragSpec } from "../../utils/DnDUtils";
import useInViewport from "../../hooks/useInViewport";
import { ASSET_ITEM } from "../../constants/DnDConstants";
import When from "../utils/When";
import AsyncPreviewImage from "./AsyncPreviewImage";
import asyncPreviewStyles from "./AsyncPreviewImage.module.css";
import styles from "./AssetThumbnail.module.css";

const { ASSET_STATUS_ACTIVE, UPLOAD_STATUS_ERROR, RESOURCE_ASSET } =
  AppConstants;

// Load previews a screen ahead of the scroll so tiles are rarely blank in use.
const PREVIEW_ROOT_MARGIN = "400px";

const AssetThumbnail = ({
  asset,
  index,
  onDrop,
  onSelect,
  selected,
  draggable = false,
  scrollRootRef,
}) => {
  // Defer the preview request until the tile nears the viewport. A large
  // library would otherwise fire one request per asset the moment it opens.
  const [thumbnailRef, previewInView] = useInViewport({
    root: scrollRootRef,
    rootMargin: PREVIEW_ROOT_MARGIN,
  });
  const drag = generateDragSpec({
    type: ASSET_ITEM,
    item: {
      asset,
      index,
    },
    canDrag: () => draggable,
    onDrop: (result) => {
      // Check if the drop was already handled by another drop target
      // If result has data, it was handled by the gallery (we still want to add)
      // If result has no data, it was handled by a folder (we should skip)
      if (result && !result.data) {
        return;
      }

      if (typeof onDrop === "function") {
        onDrop(result);
      }
    },
  });

  const [_, dragRef] = useDrag(drag);

  const _select = (e, source) => {
    const { range: rangeSelect, multi: multiSelect } =
      EventUtils.selectionType(e);
    const { altKey: altAction, type: event } = e;

    const selection = {
      asset,
      index,
      source,
      rangeSelect,
      multiSelect,
      altAction,
      type: RESOURCE_ASSET,
      event,
    };

    onSelect && onSelect(selection);
  };

  const _onClick = (e) => {
    _select(e, "thumb");
  };

  const _onSelect = (e) => {
    _select(e, "check");
  };

  const _copyPath = async () => {
    let url = AssetUtils.getOriginalURL(asset.key);

    if (url && url.indexOf("//") === 0) {
      url = `https:${url}`;
    }

    await copyToClipboard({
      val: url,
      showToast: true,
      successText: "Link copied!",
    });
  };

  const isActive = asset.status === ASSET_STATUS_ACTIVE;
  const isError = asset.status === UPLOAD_STATUS_ERROR;
  const errorMessage = isError
    ? asset.message || "Failed to upload file"
    : null;
  const asyncPreview = AssetUtils.hasAsyncPreview(asset);
  const selectedClass = selected ? "selected" : "";
  let thumbnailClass = "asset-thumbnail";
  let thumbnailStyle;
  let thumbnailContent = null;

  if (isError) {
    thumbnailClass += ` ${styles.thumbnailError}`;
    thumbnailContent = (
      <div className={styles.error}>
        <Icon type="warning" size="20" />
        <span className={styles.errorMessage}>{errorMessage}</span>
      </div>
    );
  } else if (isActive && previewInView) {
    if (asyncPreview) {
      thumbnailClass += ` ${asyncPreviewStyles.thumbnail}`;
      thumbnailContent = (
        <AsyncPreviewImage asset={asset} alt={asset.asset_name} />
      );
    } else {
      thumbnailStyle = {
        backgroundImage: `url(${AssetUtils.getV2PreviewURL(asset.key)})`,
        backgroundSize: "contain",
        backgroundPosition: "50% 50%",
        backgroundRepeat: "no-repeat",
      };
    }
  }

  return (
    <div className={`asset-thumbnail-wrapper ${selectedClass}`} ref={dragRef}>
      <div
        ref={thumbnailRef}
        className={thumbnailClass}
        style={thumbnailStyle}
        onMouseDown={_onClick}
        onMouseUp={_onClick}
        title={errorMessage}
      >
        {thumbnailContent}
      </div>
      <div className="asset-label" title={asset.asset_name}>
        <span className="asset-name">{asset.asset_name}</span>
        <span className="asset-ext">.{asset.ext}</span>
      </div>
      <div className="asset-actions">
        <div className="asset-action asset-select" onClick={_onSelect}>
          <Icon type="checkFill" size="20" />
        </div>
        <When it={isActive}>
          <div className="asset-action asset-link-copy" onClick={_copyPath}>
            <Icon type="link" size="20" />
          </div>
        </When>
      </div>
    </div>
  );
};

export default AssetThumbnail;
